import { SOURCE_ANCHOR_ATTRIBUTE } from './sourceAnchor';

const ACTIVATABLE_SELECTOR = [
  'button',
  'a[href]',
  'input',
  'select',
  'textarea',
  'summary',
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="tab"]',
  '[role="option"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[role="radio"]',
].join(', ');

const isElement = (node: EventTarget): node is Element =>
  typeof (node as Element).matches === 'function';

const isInteractiveRow = (row: Element): boolean =>
  row.hasAttribute(SOURCE_ANCHOR_ATTRIBUTE) || row.hasAttribute('tabindex');

export const resolveHost = (event: Event): Element | null => {
  const path =
    typeof event.composedPath === 'function' ? event.composedPath() : [];
  for (const node of path) {
    if (isElement(node) && node.matches(ACTIVATABLE_SELECTOR)) {
      return node;
    }
  }
  const target = event.target;
  if (!target || !isElement(target)) {
    return null;
  }
  const activatable = target.closest(ACTIVATABLE_SELECTOR);
  if (activatable) {
    return activatable;
  }
  const row = target.closest('tr');
  return row && isInteractiveRow(row) ? row : null;
};

export const nativelyActivatesOnEnter = (host: Element): boolean => {
  if (host.tagName === 'BUTTON' || host.tagName === 'SUMMARY') {
    return true;
  }
  if (host.tagName === 'A' && host.hasAttribute('href')) {
    return true;
  }
  if (host.tagName === 'INPUT') {
    const type = (host as HTMLInputElement).type.toLowerCase();
    return ['button', 'checkbox', 'radio', 'reset', 'submit'].includes(type);
  }
  return false;
};

export const TEXT_ENTRY_ROLES = [
  'combobox',
  'searchbox',
  'spinbutton',
  'textbox',
];

const TEXT_INPUT_TYPES = [
  'email',
  'number',
  'password',
  'search',
  'tel',
  'text',
  'url',
];

export const isTextEntry = (host: Element): boolean => {
  if (host.tagName === 'TEXTAREA') {
    return true;
  }
  if ((host as HTMLElement).isContentEditable) {
    return true;
  }
  const role = host.getAttribute('role');
  if (role && TEXT_ENTRY_ROLES.includes(role)) {
    return true;
  }
  if (host.tagName !== 'INPUT') {
    return false;
  }
  return TEXT_INPUT_TYPES.includes(
    (host as HTMLInputElement).type.toLowerCase(),
  );
};
